"use client";

import React from "react";
import { AnimatePresence, motion, useScroll, useTransform, useMotionValueEvent } from "framer-motion";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import Image from "@/helpers/Image";
import { cn } from "@/lib/utils";
import squares from "@/public/icons/four-squares.svg";
import mediaBg from "@/public/home/how-it-works-media-bg.jpg";
import type { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

/**
 * How it works, "3 stacked steps" layout.
 * Figma: desktop 3165:28270, mobile 3807:5703.
 * Replaces HowItWorksSection on the homepage (the galaxy / marquee entrance
 * still renders on /homepage-old). Desktop keeps the pinned stage where the
 * active step follows the scroll; mobile stacks the steps.
 */

const EASE = [0.22, 1, 0.36, 1] as const;
const FADE = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -24 },
} as const;
const FADE_TRANSITION = { duration: 0.4, ease: [...EASE] };
const IMAGE_VARIANTS = {
  enter: (dir: number) => ({ x: dir > 0 ? "100%" : "-100%" }),
  center: { x: "0%" },
  exit: (dir: number) => ({ x: dir > 0 ? "-100%" : "100%" }),
};
const IMAGE_TRANSITION = { duration: 0.8, ease: [...EASE] };
const VIEWPORT = { once: true, margin: "-80px" } as const;
const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: VIEWPORT,
  transition: { duration: 0.7, ease: [...EASE], delay },
});

// Copy from the Figma frames, used until the fields are filled in Studio
const FALLBACK_CHIP = "How it works";
const FALLBACK_TITLE = "We turn your losing trades";
const FALLBACK_SUBTITLE = "into lessons you can actually use.";
const FALLBACK_TEXT =
  "Connect your broker, and we handle the rest — tracking, analysis, and the insights you need to actually improve.";

// Figma 3165:28487: 1px diagonal lines, ~10% black, 10px apart (measured on the render)
const HATCH =
  "repeating-linear-gradient(135deg, rgba(0,0,0,0.1) 0, rgba(0,0,0,0.1) 1px, transparent 1px, transparent 1.25rem)";

type Item = NonNullable<PageHomeSchema["howItWorksItems"]>[number];

const pad = (n: number) => String(n).padStart(2, "0");

// Studio step titles carry "\n" soft breaks for the desktop stage; the Figma mobile
// frame (3807:5703) lets them wrap naturally, so collapse the breaks to spaces.
const withoutSoftBreaks = (blocks: Item["title"]) =>
  blocks.map((block) =>
    block._type === "block" && Array.isArray(block.children)
      ? {
          ...block,
          children: block.children.map((child) =>
            typeof child.text === "string" ? { ...child, text: child.text.replace(/\s*\n\s*/g, " ") } : child,
          ),
        }
      : block,
  );

function StepEyebrow({ index, count, className }: { index: number; count: number; className?: string }) {
  return (
    <Chip
      text={`Step ${pad(index + 1)} / ${pad(count)}`}
      className={cn("prose-p:text-black", className)}
    />
  );
}

function StepLabel({ index, title }: { index: number; title?: string }) {
  if (!title) return null;
  return (
    <div className="flex items-center gap-[1rem] font-spacemono text-[2.25rem] leading-[110%] text-black uppercase">
      <p className="m-0">{pad(index + 1)}</p>
      <Image
        src={squares}
        alt=""
        className="!w-[1.5rem]"
      />
      <p className="m-0">{title}</p>
    </div>
  );
}

/** Figma "Media": speed-lines background, 10% black overlay, the app mock centered at 416px. */
function MediaFrame({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("relative overflow-clip bg-black", className)}>
      <Image
        src={mediaBg}
        alt=""
        quality={85}
        className="pointer-events-none absolute inset-0 !h-full !w-full object-cover"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-black/10"
      />
      {children}
    </div>
  );
}

function Mock({ image }: { image: Item["image"] }) {
  return (
    <Image
      src={image}
      alt=""
      quality={100}
      className="absolute top-1/2 left-1/2 !h-auto !w-[min(52rem,92%)] -translate-x-1/2 -translate-y-1/2"
    />
  );
}

function SectionHeader({ data, className }: { data: PageHomeSchema; className?: string }) {
  const { howItWorksChip, howItWorksTitle, howItWorksSubtitle, howItWorksText } = data;
  // The existing `howItWorksTitle` was written for the old marquee ("How it works"),
  // so the two-line headline only switches to Studio content once a subtitle is set.
  const hasHeadline = Boolean(howItWorksSubtitle);
  const title = hasHeadline ? howItWorksTitle : FALLBACK_TITLE;
  const subtitle = hasHeadline ? howItWorksSubtitle : FALLBACK_SUBTITLE;
  return (
    <div className={cn("flex flex-col items-center text-center", className)}>
      <motion.div {...reveal()}>
        <Chip
          text={howItWorksChip || FALLBACK_CHIP}
          className="prose-p:text-black"
        />
      </motion.div>
      <motion.h2
        {...reveal(0.1)}
        className="m-0 mt-[2.5rem] text-[5rem] leading-[100%] font-normal tracking-[-0.15rem] text-[#111] [text-wrap:balance] lg:mt-[3rem] lg:[text-wrap:wrap] lg:text-[8rem] lg:leading-[108%] lg:tracking-[-0.24rem] lg:text-black">
        {title}{" "}
        <span className="text-[#9a9a9a] lg:block lg:text-[#999]">{subtitle}</span>
      </motion.h2>
      <motion.p
        {...reveal(0.2)}
        className="m-0 mt-[2.5rem] text-[2rem] leading-[150%] text-black/70 lg:hidden">
        {howItWorksText || FALLBACK_TEXT}
      </motion.p>
    </div>
  );
}

// ─── Mobile: stacked steps ───────────────────────────────────────────────────

function HowItWorksStepsMobile({ data, items }: { data: PageHomeSchema; items: Item[] }) {
  return (
    <section className="relative overflow-x-clip bg-white lg:hidden">
      <SectionHeader
        data={data}
        className="border-b border-black/10 px-[3rem] py-[6rem]"
      />
      {items.map((item, i) => {
        const cta = item.cta?.cta ? item.cta : items[0]?.cta;
        return (
          <motion.article
            key={i}
            {...reveal()}
            className={cn(
              "flex flex-col gap-[2.5rem] border-b border-black/10 px-[3rem] pt-[5rem] pb-[5rem]",
              i === items.length - 1 && "border-b-0 pb-[8rem]",
            )}>
            <StepEyebrow
              index={i}
              count={items.length}
            />
            {item.title && (
              <div className="richtext richtext-start max-w-[46rem] prose-h1:m-0 prose-h1:text-[4rem] prose-h1:leading-[104%] prose-h1:font-normal prose-h1:tracking-[-0.12rem] prose-h1:text-[#111] prose-h2:m-0 prose-h2:text-[4rem] prose-h2:leading-[104%] prose-h2:font-normal prose-h2:tracking-[-0.12rem] prose-h2:text-[#111]">
                <PortableText
                  value={withoutSoftBreaks(item.title)}
                  components={richTextComponent}
                />
              </div>
            )}
            {item.image && (
              <MediaFrame className="aspect-[382/440] w-full rounded-[1rem]">
                <Mock image={item.image} />
              </MediaFrame>
            )}
            <StepLabel
              index={i}
              title={item.description?.title}
            />
            {item.description?.content && (
              <div className="richtext richtext-start -mt-[0.5rem] prose-p:m-0 prose-p:text-[2rem] prose-p:leading-[150%] prose-p:text-[#161616]">
                <PortableText
                  value={item.description.content}
                  components={richTextComponent}
                />
              </div>
            )}
            {cta && (
              <CTAButton
                data={cta}
                overrideVariant="orange"
                className="mt-[0.5rem] [&_.cta-wrapper]:w-full [&_.button]:py-[1.5rem]"
              />
            )}
          </motion.article>
        );
      })}
    </section>
  );
}

// ─── Desktop: pinned stage, active step follows the scroll ───────────────────

function StepBar({
  progress,
  index,
  count,
}: {
  progress: ReturnType<typeof useScroll>["scrollYProgress"];
  index: number;
  count: number;
}) {
  // 0 → 1 while this step is the active one, 1 once it is done
  const fill = useTransform(progress, (p) => Math.max(0, Math.min(1, p * count - index)));
  return (
    <div className="relative w-[0.375rem] flex-1 overflow-clip rounded-full bg-black/10">
      <motion.div
        className="absolute inset-0 origin-top rounded-full bg-[#FF5500]"
        style={{ scaleY: fill }}
      />
    </div>
  );
}

function HowItWorksStepsDesktop({ data, items }: { data: PageHomeSchema; items: Item[] }) {
  const count = items.length || 1;
  const rangeRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: rangeRef,
    offset: ["start start", "end end"],
  });

  const [active, setActive] = React.useState(0);
  const [dir, setDir] = React.useState(1);
  useMotionValueEvent(scrollYProgress, "change", (p) => {
    const next = Math.min(Math.floor(p * count), count - 1);
    setActive((prev) => {
      if (prev !== next) setDir(next > prev ? 1 : -1);
      return next;
    });
  });

  const item = items[active];
  const cta = item?.cta?.cta ? item.cta : items[0]?.cta;
  return (
    <section className="relative hidden overflow-x-clip border-t border-black/10 lg:block">
      <div
        ref={rangeRef}
        style={{ height: `${count * 100 + 100}vh` }}>
        {/*
         * Pinned 100vh frame, sized after Figma 3165:28270 (1920×1303):
         * header 252px, stage 810px, fade strip 241px. The viewport is
         * shorter than that, so the strip gives way first (flex-shrink 999),
         * then the stage; the media only scales once the stage is tighter
         * than the mock itself. The stage keeps the Figma width (1439px =
         * 180rem) and stays centered, so on wide rem viewports (>1920px
         * the root font-size follows 800vw/2200) the hatch gutters grow
         * instead of the columns drifting apart. Line tones measured on the
         * Figma render: header 20%, strip 10%, vertical hairlines 14%.
         */}
        <div className="sticky top-0 flex h-screen flex-col overflow-clip">
          {/* Figma 3807:5706: two vertical hairlines (14% black) at the stage edges, full frame height */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 left-[calc(50%-90rem)] z-[3] w-px bg-black/[0.14]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 right-[calc(50%-90rem)] z-[3] w-px bg-black/[0.14]"
          />
          <div className="relative flex min-h-0 grow flex-col">
            {/* Header */}
            <SectionHeader
              data={data}
              className="shrink-0 border-b border-black/20 px-[3rem] pt-[5.375rem] pb-[5.375rem]"
            />

            {/* Stage row: hatched gutters + centered white stage (Figma 810px = 101.25rem tall) */}
            <div className="relative flex min-h-0 grow basis-[101.25rem]">
              <div
                aria-hidden="true"
                className="min-w-0 flex-1"
                style={{ backgroundImage: HATCH }}
              />
              <motion.div
                {...reveal(0.15)}
                className="flex w-[180rem] max-w-full shrink-0 items-center justify-between bg-white px-[4rem]">
                {/* Left: eyebrow + title + CTA */}
                <div className="flex w-[50.375rem] shrink-0 flex-col items-start gap-[3rem]">
                  <StepEyebrow
                    index={active}
                    count={count}
                  />
                  <div className="relative min-h-[16.25rem] w-full">
                    <AnimatePresence
                      initial={false}
                      mode="wait">
                      <motion.div
                        key={"hiw-title-" + active}
                        {...FADE}
                        transition={FADE_TRANSITION}
                        className="richtext richtext-start prose-h1:m-0 prose-h1:bg-gradient-to-b prose-h1:from-black prose-h1:to-[#353535] prose-h1:bg-clip-text prose-h1:text-[8rem] prose-h1:leading-[100%] prose-h1:font-normal prose-h1:tracking-[-0.15rem] prose-h1:text-transparent prose-h2:m-0 prose-h2:bg-gradient-to-b prose-h2:from-black prose-h2:to-[#353535] prose-h2:bg-clip-text prose-h2:text-[8rem] prose-h2:leading-[100%] prose-h2:font-normal prose-h2:tracking-[-0.15rem] prose-h2:text-transparent">
                        {item?.title && (
                          <PortableText
                            value={item.title}
                            components={richTextComponent}
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  {cta && (
                    <CTAButton
                      data={cta}
                      overrideVariant="orange"
                    />
                  )}
                </div>

                {/* Center: media (466×543 in Figma), mock slides on step change */}
                <MediaFrame className="aspect-[466/543] h-[67.875rem] max-h-full w-auto shrink-0">
                  <AnimatePresence
                    initial={false}
                    custom={dir}>
                    {item?.image && (
                      <motion.div
                        key={"hiw-img-" + active}
                        custom={dir}
                        variants={IMAGE_VARIANTS}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={IMAGE_TRANSITION}
                        className="absolute inset-0">
                        <Mock image={item.image} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </MediaFrame>

                {/* Right: step bars + label + description */}
                <div className="flex w-[50.75rem] shrink-0 items-stretch gap-[3rem]">
                  <div className="flex shrink-0 flex-col gap-[1.25rem] self-stretch">
                    {items.map((_, i) => (
                      <StepBar
                        key={i}
                        progress={scrollYProgress}
                        index={i}
                        count={count}
                      />
                    ))}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-[3rem]">
                    <div className="relative h-[2.5rem] overflow-clip">
                      <AnimatePresence
                        initial={false}
                        mode="wait">
                        <motion.div
                          key={"hiw-label-" + active}
                          {...FADE}
                          transition={FADE_TRANSITION}>
                          <StepLabel
                            index={active}
                            title={item?.description?.title}
                          />
                        </motion.div>
                      </AnimatePresence>
                    </div>
                    <hr className="m-0 w-full border-t border-black/25" />
                    <div className="relative h-[15.625rem] overflow-clip">
                      <AnimatePresence
                        initial={false}
                        mode="wait">
                        <motion.div
                          key={"hiw-desc-" + active}
                          {...FADE}
                          transition={FADE_TRANSITION}
                          className="richtext richtext-start prose-p:m-0 prose-p:text-[2.5rem] prose-p:leading-[150%] prose-p:text-black">
                          {item?.description?.content && (
                            <PortableText
                              value={item.description.content}
                              components={richTextComponent}
                            />
                          )}
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </motion.div>
              <div
                aria-hidden="true"
                className="min-w-0 flex-1"
                style={{ backgroundImage: HATCH }}
              />
            </div>
          </div>

          {/* Bottom strip (Figma 241px, 10% line): collapses first when the viewport is short */}
          <div
            aria-hidden="true"
            className="min-h-0 shrink-[999] basis-[30rem] border-t border-black/10"
          />
        </div>
      </div>
    </section>
  );
}

export default function HowItWorksStepsSection({ data }: { data: PageHomeSchema }) {
  const items = data.howItWorksItems ?? [];
  if (items.length === 0) return null;
  return (
    <div id="how-it-works">
      <HowItWorksStepsMobile
        data={data}
        items={items}
      />
      <HowItWorksStepsDesktop
        data={data}
        items={items}
      />
    </div>
  );
}
